/**
 * Validación humana del tipo de evento de un correo entrante vinculado a una petición.
 * La IA propone (tipo + confianza + motivos); el usuario confirma o corrige el tipo (y un evento secundario),
 * ve la acción exacta que se aplicará y sólo entonces pulsa «Confirmar y aplicar». Nada toca la petición antes.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export const TIPOS_EVENTO = {
  nueva_peticion: "Nueva petición", anadir_espacio: "Añadir espacio / punto nuevo", anadir_linea: "Añadir línea en espacio existente",
  modificar_espacio_linea: "Modificar espacio / línea", permisos_confirmacion: "Permisos / autorizaciones", aaff_recibidos: "AAFF recibidos",
  material_enviado: "Material enviado", cambio_fecha_horario: "Cambio de fecha / horario", solicitud_presupuesto: "Solicitud de presupuesto", otro_revision: "Otro (revisión)",
};

function Preview({ pv }) {
  if (!pv) return <div className="text-zinc-500">Sin vista previa para este tipo</div>;
  return (
    <div className="space-y-1" data-testid="validacion-preview">
      <div className="text-sky-200" data-testid="validacion-preview-resumen">{pv.resumen}</div>
      {pv.faltan && <div className="text-amber-200 flex gap-1 items-start" data-testid="validacion-preview-faltan"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{pv.faltan}</div>}
      {pv.espacios_nuevos?.length > 0 && <div>Ubicaciones nuevas: <b>{pv.espacios_nuevos.join(", ")}</b></div>}
      {pv.espacios_existentes_mencionados?.length > 0 && <div>Espacios existentes mencionados: {pv.espacios_existentes_mencionados.join(", ")}</div>}
      {pv.lineas_actuales != null && pv.tipo?.startsWith("anadir") && <div className="text-zinc-400">{pv.lineas_actuales} línea(s) actuales se conservan intactas</div>}
      {pv.tipo === "aaff_recibidos" && (
        <div className="space-y-0.5">
          <div>URL AAFF: {pv.url_aaff ? <a href={pv.url_aaff} target="_blank" rel="noreferrer" className="underline text-fuchsia-300 break-all" data-testid="validacion-preview-url">{pv.url_aaff}</a> : <span className="text-amber-200">ninguna fiable</span>}</div>
          {pv.aaff_url_actual && <div className={pv.conflicto_aaff_url ? "text-amber-200" : "text-zinc-400"}>aaff_url actual: {pv.aaff_url_actual} {pv.conflicto_aaff_url ? "→ NO se sobrescribe, se anota" : ""}</div>}
          <div className="text-zinc-400">Adjuntos reales: {pv.adjuntos_reales?.length ? pv.adjuntos_reales.join(", ") : "ninguno"}{pv.adjuntos_inline_ignorados > 0 && ` · ${pv.adjuntos_inline_ignorados} inline de firma ignorados`} · aviso a {pv.aviso_a}</div>
        </div>
      )}
      {pv.cambios && (
        <div className="space-y-0.5">
          {pv.resumen_ia && <div className="text-zinc-400 italic">{pv.resumen_ia}</div>}
          {pv.cambios.length > 0 && (
            <table className="w-full text-[11px]" data-testid="validacion-preview-cambios"><tbody>
              {pv.cambios.map((c, i) => <tr key={i} className="border-t border-zinc-800"><td className="py-0.5 pr-2 text-zinc-400">{c.espacio}{c.ciudad ? ` (${c.ciudad})` : ""}</td><td className="pr-2">{c.campo}</td><td className="pr-2 text-zinc-500 line-through">{c.actual || "—"}</td><td className="text-emerald-200">{c.propuesto}{c.revisar ? " ⚠" : ""}</td></tr>)}
            </tbody></table>
          )}
          {pv.avisos?.length > 0 && <div className="text-amber-200">{pv.avisos.join(" · ")}</div>}
          {pv.sin_match?.length > 0 && <div className="text-zinc-500">Sin línea coincidente: {pv.sin_match.map((s) => s.espacio_nombre || s.texto || JSON.stringify(s)).join(", ")}</div>}
        </div>
      )}
    </div>
  );
}

export default function ValidacionEvento({ d, onDone }) {
  const [tipo, setTipo] = useState(d.tipo_evento_predicho || d.tipo_evento || "otro_revision");
  const [sec, setSec] = useState(d.evento_secundario_detectado?.tipo_evento || "");
  const [previews, setPreviews] = useState(d.previews || {});
  const [loading, setLoading] = useState(null);
  const [busy, setBusy] = useState(false);

  const cargar = async (t) => {
    if (!t || previews[t]) return;
    setLoading(t);
    try { const { data } = await api.post(`/inbound/${d.id}/preview-accion`, { tipo_evento: t }); setPreviews((p) => ({ ...p, [t]: data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular la vista previa"); }
    finally { setLoading(null); }
  };
  useEffect(() => { cargar(tipo); }, [tipo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sec) cargar(sec); }, [sec]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inbound/${d.id}/confirmar-evento`, { tipo_evento_confirmado: tipo, eventos_secundarios: sec ? [sec] : [] });
      if (data?.ya_confirmado) toast.info("Este correo ya estaba confirmado; no se ha duplicado nada");
      else toast.success(tipo === (d.tipo_evento_predicho || d.tipo_evento) ? "Evento confirmado y aplicado" : "Corrección registrada y aplicada");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al aplicar"); }
    finally { setBusy(false); }
  };
  const FAMILIA_BORRADOR = ["anadir_espacio", "anadir_linea", "nueva_peticion"];
  const secundarioValido = (k) => k !== tipo && !(FAMILIA_BORRADOR.includes(k) && FAMILIA_BORRADOR.includes(tipo));
  const cambiarTipo = (t) => {
    setTipo(t);
    if (t === sec) setSec(tipo);
    else if (sec && FAMILIA_BORRADOR.includes(sec) && FAMILIA_BORRADOR.includes(t)) setSec("");
  };
  const corrige = tipo !== (d.tipo_evento_predicho || d.tipo_evento);
  const sel = "h-8 text-xs rounded border border-zinc-700 bg-zinc-900 px-2 w-full";
  return (
    <div className="rounded-lg border border-violet-500/40 p-3 space-y-2 text-xs bg-violet-950/20" data-testid="validacion-evento">
      <div className="font-medium text-violet-200">Validación humana · la IA propone, tú confirmas</div>
      <div data-testid="validacion-tipo-sugerido">Tipo sugerido: <b>{TIPOS_EVENTO[d.tipo_evento_predicho || d.tipo_evento] || d.tipo_evento}</b> · confianza {Math.round((d.confianza_evento || 0) * 100)}%
        {d.evento_secundario_detectado && <> · secundario sugerido: <b>{TIPOS_EVENTO[d.evento_secundario_detectado.tipo_evento]}</b> ({Math.round((d.evento_secundario_detectado.confianza_evento || 0) * 100)}%)</>}</div>
      <div className="text-zinc-400" data-testid="validacion-motivos">{(d.motivos_evento || []).join(" · ")}</div>
      <div className="grid md:grid-cols-2 gap-2">
        <label className="space-y-1"><span className="text-zinc-300">Tipo de evento confirmado</span>
          <select className={sel} value={tipo} onChange={(e) => cambiarTipo(e.target.value)} data-testid="validacion-tipo-select">
            {Object.entries(TIPOS_EVENTO).map(([k, l]) => <option key={k} value={k}>{l}{k === (d.tipo_evento_predicho || d.tipo_evento) ? " (sugerido)" : ""}</option>)}
          </select></label>
        <label className="space-y-1"><span className="text-zinc-300">Evento secundario (opcional)</span>
          <select className={sel} value={sec} onChange={(e) => setSec(e.target.value)} data-testid="validacion-secundario-select">
            <option value="">— ninguno —</option>
            {Object.entries(TIPOS_EVENTO).filter(([k]) => secundarioValido(k)).map(([k, l]) => <option key={k} value={k}>{l}{k === d.evento_secundario_detectado?.tipo_evento ? " (detectado)" : ""}</option>)}
          </select></label>
      </div>
      {corrige && <div className="text-amber-200" data-testid="validacion-correccion">Cambias el tipo ({TIPOS_EVENTO[d.tipo_evento_predicho || d.tipo_evento]} → {TIPOS_EVENTO[tipo]}): se registrará como corrección para el aprendizaje del cliente</div>}
      <div className="rounded border border-zinc-700 p-2 space-y-2">
        <div className="text-zinc-300 font-medium">Acción propuesta · {TIPOS_EVENTO[tipo]}</div>
        {loading === tipo ? <div className="flex items-center gap-1 text-zinc-400"><Loader2 className="w-3 h-3 animate-spin" />Calculando vista previa…</div> : <Preview pv={previews[tipo]} />}
        {sec && (<><div className="text-zinc-300 font-medium border-t border-zinc-800 pt-2">Segunda acción · {TIPOS_EVENTO[sec]}</div>
          {loading === sec ? <div className="flex items-center gap-1 text-zinc-400"><Loader2 className="w-3 h-3 animate-spin" />Calculando…</div> : <Preview pv={previews[sec]} />}</>)}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || !!loading} onClick={confirmar} data-testid="validacion-confirmar-aplicar">{busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}{busy ? "Aplicando… la IA prepara la propuesta (~1 min)" : "Confirmar y aplicar"}</Button>
        {!busy && <span className="text-zinc-500">Hasta aquí nada ha tocado la petición {d.peticion?.codigo}</span>}
      </div>
    </div>
  );
}
